// src/services/forumService.ts

import { API_URL } from '../config';

export interface ForumPost {
    id: number;
    topic_id: number;
    author_id: number;
    author_name: string;
    content: string;
    is_opener: boolean;
    likes_count: number;
    liked_by_me: boolean;
    created_at: string;
    updated_at: string;
}

export interface ForumTopic {
    id: number;
    title: string;
    category: string;
    stock_symbol: string | null;
    author_id: number;
    author_name: string;
    views_count: number;
    replies_count: number;
    created_at: string;
    last_activity_at: string;
}

export interface ForumTopicDetail extends ForumTopic {
    posts: ForumPost[];
}

export interface ForumTopicListResponse {
    topics: ForumTopic[];
    total_count: number;
}

const getHeaders = () => {
    const token = localStorage.getItem('access_token');
    return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
    };
};

export const fetchTopics = async (skip = 0, limit = 20, category?: string, stockSymbol?: string): Promise<ForumTopicListResponse> => {
    let url = `${API_URL}/v1/forum/topics?skip=${skip}&limit=${limit}`;
    if (category) url += `&category=${category}`;
    if (stockSymbol) url += `&stock_symbol=${stockSymbol}`;

    const response = await fetch(url, { headers: getHeaders() });
    if (!response.ok) {
        throw new Error('Failed to fetch forum topics');
    }
    return response.json();
};

export const fetchTopicDetail = async (topicId: number): Promise<ForumTopicDetail> => {
    const response = await fetch(`${API_URL}/v1/forum/topics/${topicId}`, { headers: getHeaders() });
    if (!response.ok) {
        throw new Error('Failed to fetch forum topic detail');
    }
    return response.json();
};

export const createTopic = async (title: string, category: string, content: string, stockSymbol?: string): Promise<ForumTopicDetail> => {
    const payload = {
        title,
        category,
        content,
        stock_symbol: stockSymbol || null
    };

    const response = await fetch(`${API_URL}/v1/forum/topics`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(payload)
    });

    if (!response.ok) {
        throw new Error('Failed to create forum topic');
    }
    return response.json();
};

export const createPost = async (topicId: number, content: string): Promise<ForumPost> => {
    const payload = {
        topic_id: topicId,
        content
    };

    const response = await fetch(`${API_URL}/v1/forum/posts`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(payload)
    });

    if (!response.ok) {
        throw new Error('Failed to create forum post');
    }
    return response.json();
};

export const togglePostLike = async (postId: number): Promise<{ post_id: number; likes_count: number; liked: boolean }> => {
    const response = await fetch(`${API_URL}/v1/forum/posts/${postId}/like`, {
        method: 'POST',
        headers: getHeaders(),
    });
    if (!response.ok) {
        throw new Error('Failed to toggle like');
    }
    return response.json();
};

export const deleteTopic = async (topicId: number): Promise<{ message: string }> => {
    const response = await fetch(`${API_URL}/v1/forum/topics/${topicId}`, {
        method: 'DELETE',
        headers: getHeaders(),
    });
    if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to delete topic');
    }
    return response.json();
};

export const deletePost = async (postId: number): Promise<{ message: string }> => {
    const response = await fetch(`${API_URL}/v1/forum/posts/${postId}`, {
        method: 'DELETE',
        headers: getHeaders(),
    });
    if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to delete post');
    }
    return response.json();
};
